import { Typography } from '@cp/ds/src/components/typography';
import React from 'react';
import { Stack } from '@cp/ds/src/components/stack';
import { FormProvider, useForm } from 'react-hook-form';
import { FormField, useYupValidationResolver } from '@cp/ds/src/components/form';
import { StyledUnorderedList } from '@cp/ds/src/components/styled/styledUnorderedList';
import { StyledListItem } from '@cp/ds/src/components/styled/styledListItem';
import { useTheme } from '@cp/ds/src/hooks/useTheme';
import { Box } from '@cp/ds/src/components/box';
import { ContentSkeleton } from '@cp/ds/src/components/contentSkeleton';
import { ModalLayout } from '@cp/ds/src/components/modalLayout';
import { SxProps } from '@cp/ds';
import { coreLocationApi, coreLocationLinkingCitiesListApi, useCurrentCity } from '@cp/entities/location';
import { GenerateAppPathProps, usePathGenerator } from '@cp/entities/clientWebRoutes';
import { CityViewModel } from '@common/model/location/cityViewModel';
import { ClientWebPage } from '@common/model/meta/clientWebPage';
import { ClientWebPageProps } from '@common/model/meta/clientWebPageProps';
import { CatalogItemType } from '@common/model/catalog/item/type';
import { mergeSx } from '@cp/ds/src/utils/sx/merge';
import { RoutedLink as Link } from 'src/shared/ui/link';
import { CitySearchFormModel } from './model';
import { CITY_SEARCH_QUERY_FORM_NAME, CITY_SEARCH_QUERY_MIN_LENGTH } from './const';
import { citySearchFormSchema } from './validation';

export interface CitySelectionFormProps {
  onClose: () => void;
  onCitySelected?: (city: CityViewModel) => void;
  target?: React.RefObject<HTMLElement>;
  generateCityLinkProps?: Partial<GenerateAppPathProps<ClientWebPage>>;
  pageProps?: ClientWebPageProps[ClientWebPage.Catalog];
}

const TOP_CITIES_LIMIT = 20;

const CitySelectionForm: React.FC<CitySelectionFormProps> = (props) => {
  const { onClose, onCitySelected, target, generateCityLinkProps, pageProps } = props;
  const theme = useTheme();
  const generateAppPath = usePathGenerator();
  const { currentCity } = useCurrentCity({ allowEmptyValue: true });
  const resolver = useYupValidationResolver<CitySearchFormModel>(citySearchFormSchema);
  const form = useForm<{ citySearchQuery: string }>({
    defaultValues: {
      citySearchQuery: '',
    },
    resolver,
  });

  const sxPositionModalProps: SxProps = {
    position: { xs: 'relative', md: 'fixed' },
    top: target?.current?.offsetParent?.clientHeight,
    left: target?.current?.offsetLeft,
  };

  const query = form.watch(CITY_SEARCH_QUERY_FORM_NAME, '');
  const normalizedQuery = query ?? '';
  const { data: searchCitiesList } = coreLocationApi.useGetCitiesQuery(
    { name: normalizedQuery },
    { skip: normalizedQuery.length < CITY_SEARCH_QUERY_MIN_LENGTH },
  );

  const isCatalogMode = Boolean(pageProps);
  const { data: mainLinkingList, isLoading: isMainLinkingLoading } = coreLocationLinkingCitiesListApi.useGetMainLinkingCitiesListQuery(
    {
      cityId: currentCity?.id ?? 0,
      catalogType: pageProps?.catalogType ?? CatalogItemType.Resume,
      careerId: pageProps?.careerId ?? 0,
      filter: pageProps?.filter,
    },
    { skip: !isCatalogMode || !currentCity },
  );
  const { data: topCitiesList, isLoading: isTopCitiesLoading } = coreLocationApi.useGetCitiesQuery(
    { onlyBigCities: true },
    { skip: isCatalogMode },
  );
  const topCities = React.useMemo(() => topCitiesList?.slice(0, TOP_CITIES_LIMIT), [topCitiesList]);
  const isSearching = Boolean(searchCitiesList?.length) && normalizedQuery.length > CITY_SEARCH_QUERY_MIN_LENGTH;
  const baseList = isCatalogMode ? mainLinkingList : topCities;
  const cityList = isSearching ? searchCitiesList : baseList;
  const isLoading = isCatalogMode ? isMainLinkingLoading : isTopCitiesLoading;

  const clickHandler = (city: CityViewModel) => {
    if (onCitySelected) {
      onCitySelected(city);
    }
    onClose();
  };

  const getCityHref = (city: CityViewModel) => {
    if (!generateCityLinkProps?.page) {
      return undefined;
    }

    const { page, pageProps: p, pathParams, queryParams } = generateCityLinkProps;
    return generateAppPath({
      page,
      pageProps: p,
      pathParams: { ...pathParams, city: city.alias },
      queryParams,
    });
  };

  return (
    <ModalLayout
      containerSx={mergeSx(
        {
          p: 0,
          backgroundColor: theme.palette['gray background'],
          maxWidth: 'fit-content',
          height: 'fit-content',
          borderRadius: '24px',
        },
        target ? sxPositionModalProps : {},
      )}
      isMobile={false}
      onClose={onClose}
    >
      <Stack
        pb={{ xs: 2, md: 5 }}
        pt={{ xs: 2.5, md: 3.75 }}
        px={{ xs: 2.5, md: 8.25 }}
        sx={{ maxHeight: 382, height: { xs: 338, md: 382 }, minWidth: { md: 430 } }}
      >
        <Box sx={{ textAlign: { xs: 'center', md: 'start' }, mb: { xs: 3, md: 3.75 } }}>
          <Typography variant="h6 Bold">Выбор города</Typography>
        </Box>
        <FormProvider {...form}>
          <FormField autoFocus name="citySearchQuery" placeholder="" sx={{ mb: 1 }} type="text" />
        </FormProvider>
        <Stack sx={{ overflowY: 'auto' }}>
          {isLoading ? (
            <ContentSkeleton />
          ) : (
            <StyledUnorderedList>
              {!isSearching && currentCity ? (
                <Typography
                  component="li"
                  onClick={onClose}
                  role="button"
                  sx={{
                    py: 1,
                    pl: { xs: 0, md: 3 },
                    cursor: 'pointer',
                    borderRadius: `${theme.borderRadius.normal}px`,
                    '&:hover': { backgroundColor: theme.palette['gray ultra light'] },
                  }}
                  tabIndex={0}
                  variant="body bold"
                >
                  {currentCity.translation.name}
                </Typography>
              ) : null}
              {cityList?.map((city) => {
                if (currentCity?.alias === city.alias) {
                  return;
                }
                const href = getCityHref(city);

                return (
                  <StyledListItem key={city.id}>
                    <Link
                      href={href}
                      onClick={() => clickHandler(city)}
                      sx={{
                        display: 'flex',
                        width: 1,
                        py: 1,
                        pl: { xs: 0, md: 3 },
                        borderRadius: `${theme.borderRadius.normal}px`,
                        '&:hover': { backgroundColor: theme.palette['gray ultra light'], color: theme.palette['brand gray'] },
                      }}
                      underline={false}
                    >
                      <Typography>{city.translation.name}</Typography>
                    </Link>
                  </StyledListItem>
                );
              })}
            </StyledUnorderedList>
          )}
        </Stack>
      </Stack>
    </ModalLayout>
  );
};

export default CitySelectionForm;
